import React, { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Textarea } from "@/components/ui/textarea";
import { ScrollArea } from "@/components/ui/scroll-area";
import { IMaskInput } from 'react-imask';
import { isValidCPF, isValidCNPJ } from "@brazilian-utils/brazilian-utils";
import { createClient, updateClient, ClientFormData } from "@/services/ClientService";

interface ClientFormProps {
  client?: any;
  onSuccess?: () => void;
}

const clientSchema = z.object({
  documentType: z.enum(["cpf", "cnpj"]),
  document: z.string()
    .refine(val => {
      const cleaned = val.replace(/\D/g, '');
      if (cleaned.length === 11) {
        return isValidCPF(cleaned);
      } else if (cleaned.length === 14) {
        return isValidCNPJ(cleaned);
      }
      return false;
    }, { message: "Documento inválido" }),
  name: z.string().min(3, { message: "Nome deve ter pelo menos 3 caracteres" }),
  email: z.string().email({ message: "Email inválido" }).optional().or(z.literal("")),
  phone: z.string().optional(),
  address: z.object({
    street: z.string().optional(),
    number: z.string().optional(),
    complement: z.string().optional(),
    neighborhood: z.string().optional(),
    city: z.string().optional(),
    state: z.string().optional(),
    zipCode: z.string().optional(),
  }).optional(),
  notes: z.string().optional(),
});

type ClientFormValues = z.infer<typeof clientSchema>;

export default function ClientForm({ client, onSuccess }: ClientFormProps) {
  const queryClient = useQueryClient();

  const defaultValues: Partial<ClientFormValues> = client ? {
    documentType: client.document_type || "cpf",
    document: client.document || "",
    name: client.name || "",
    email: client.email || "",
    phone: client.phone || "",
    notes: client.notes || "",
    address: client.address || {}
  } : {
    documentType: "cpf",
    document: "",
    name: "",
    email: "",
    phone: "",
    notes: "",
    address: {}
  };

  const form = useForm<ClientFormValues>({
    resolver: zodResolver(clientSchema),
    defaultValues,
  });

  // Mutation para criar cliente
  const createClientMutation = useMutation({
    mutationFn: createClient,
    onSuccess: () => {
      // Invalida as queries de clientes para recarregar a lista
      queryClient.invalidateQueries({ queryKey: ['clients'] });
      if (onSuccess) {
        onSuccess();
      }
    },
    onError: (error) => {
      console.error("Error creating client:", error);
    }
  });

  // Mutation para atualizar cliente
  const updateClientMutation = useMutation({
    mutationFn: updateClient,
    onSuccess: () => {
      // Invalida as queries de clientes para recarregar a lista
      queryClient.invalidateQueries({ queryKey: ['clients'] });
      if (onSuccess) {
        onSuccess();
      }
    },
    onError: (error) => {
      console.error("Error updating client:", error);
    }
  });

  const onSubmit = async (data: ClientFormValues) => {
    const clientData: ClientFormData = {
      name: data.name,
      document_type: data.documentType,
      document: data.document.replace(/\D/g, ''),
      email: data.email || undefined,
      phone: data.phone || undefined,
      address: data.address,
      notes: data.notes || undefined,
    };

    if (client?.id) {
      clientData.id = client.id;
      updateClientMutation.mutate(clientData);
    } else {
      createClientMutation.mutate(clientData);
    }
  };

  const isSubmitting = createClientMutation.isPending || updateClientMutation.isPending;

  const documentType = form.watch("documentType");

  useEffect(() => {
    const subscription = form.watch((value, { name }) => {
      if (name === "address.zipCode") {
        const zipCode = value.address?.zipCode?.replace(/\D/g, "");
        
        if (zipCode && zipCode.length === 8) {
          fetch(`https://viacep.com.br/ws/${zipCode}/json/`)
            .then(res => res.json())
            .then(data => {
              if (!data.erro) {
                form.setValue("address.street", data.logradouro || "");
                form.setValue("address.neighborhood", data.bairro || "");
                form.setValue("address.city", data.localidade || "");
                form.setValue("address.state", data.uf || "");
              }
            })
            .catch(err => console.error("Erro ao buscar CEP:", err));
        }
      }
    });
    
    return () => subscription.unsubscribe();
  }, [form]);

  return (
    <div className="max-h-[80vh] flex flex-col dark:text-gray-700 dark:bg-white">
      <ScrollArea className="flex-1">
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="p-3 space-y-3">
            <FormField
              control={form.control}
              name="documentType"
              render={({ field }) => (
                <FormItem className="space-y-3">
                  <FormLabel className="dark:text-gray-700">Tipo de Documento *</FormLabel>
                  <FormControl>
                    <RadioGroup
                      onValueChange={field.onChange}
                      defaultValue={field.value}
                      className="flex flex-row space-x-4"
                    >
                      <div className="flex items-center space-x-2">
                        <RadioGroupItem value="cpf" id="cpf" className="border-[#072a45] text-[#072a45]" />
                        <label htmlFor="cpf" className="text-sm font-medium leading-none">
                          CPF (Pessoa Física)
                        </label>
                      </div>
                      <div className="flex items-center space-x-2">
                        <RadioGroupItem value="cnpj" id="cnpj" className="border-[#072a45] text-[#072a45]"/>
                        <label htmlFor="cnpj" className="text-sm font-medium leading-none">
                          CNPJ (Pessoa Jurídica)
                        </label>
                      </div>
                    </RadioGroup>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="document"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="dark:text-gray-700">{documentType === "cpf" ? "CPF *" : "CNPJ *"}</FormLabel>
                    <FormControl>
                      <IMaskInput
                        mask={documentType === "cpf" ? "000.000.000-00" : "00.000.000/0000-00"}
                        value={field.value}
                        onAccept={(value) => field.onChange(value)}
                        onBlur={field.onBlur}
                        placeholder={documentType === "cpf" ? "000.000.000-00" : "00.000.000/0000-00"}
                        className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 dark:bg-white"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="name"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="dark:text-gray-700">{documentType === "cpf" ? "Nome Completo *" : "Razão Social *"}</FormLabel>
                    <FormControl>
                      <Input className="dark:bg-white" {...field} placeholder={documentType === "cpf" ? "João da Silva" : "Empresa XYZ Ltda"} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <FormField
              control={form.control}
              name="email"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="dark:text-gray-700">Email</FormLabel>
                  <FormControl>
                    <Input className="dark:bg-white" {...field} type="email" placeholder="email@exemplo.com" />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="phone"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="dark:text-gray-700">Telefone</FormLabel>
                    <FormControl>
                      <IMaskInput
                        mask="(00) 00000-0000"
                        value={field.value || ""}
                        onAccept={(value) => field.onChange(value)}
                        onBlur={field.onBlur}
                        placeholder="(00) 00000-0000"
                        className="flex h-10 w-full dark:bg-white rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="address.zipCode"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="dark:text-gray-700">CEP</FormLabel>
                    <FormControl>
                      <IMaskInput
                        mask="00000-000"
                        value={field.value || ""}
                        onAccept={(value) => field.onChange(value)}
                        onBlur={field.onBlur}
                        placeholder="00000-000"
                        className="flex h-10 w-full dark:bg-white rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <FormField
                control={form.control}
                name="address.street"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="dark:text-gray-700">Endereço</FormLabel>
                    <FormControl>
                      <Input className="dark:bg-white" {...field} value={field.value || ""} placeholder="Rua, Avenida, etc." />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="address.number"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="dark:text-gray-700">Número</FormLabel>
                    <FormControl>
                      <Input className="dark:bg-white" {...field} value={field.value || ""} placeholder="123" />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="address.complement"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="dark:text-gray-700">Complemento</FormLabel>
                    <FormControl>
                      <Input className="dark:bg-white" {...field} value={field.value || ""} placeholder="Apto, Sala, etc." />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <FormField
                control={form.control}
                name="address.neighborhood"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="dark:text-gray-700">Bairro</FormLabel>
                    <FormControl>
                      <Input className="dark:bg-white" {...field} value={field.value || ""} placeholder="Bairro" />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="address.city"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="dark:text-gray-700">Cidade</FormLabel>
                    <FormControl>
                      <Input className="dark:bg-white" {...field} value={field.value || ""} placeholder="Cidade" />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="address.state"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="dark:text-gray-700">Estado</FormLabel>
                    <FormControl>
                      <Input className="dark:bg-white" {...field} value={field.value || ""} placeholder="UF" maxLength={2} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <FormField
              control={form.control}
              name="notes"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="dark:text-gray-700">Observações</FormLabel>
                  <FormControl>
                    <Textarea 
                      {...field} 
                      placeholder="Informações adicionais sobre o cliente" 
                      className="resize-none dark:bg-white"
                      rows={2}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </form>
        </Form>
      </ScrollArea>
      
      <div className="flex justify-end space-x-2 pt-4 border-t">
        <Button type="button" variant="outline" onClick={onSuccess} className="dark:text-white">
          Cancelar
        </Button>
        <Button 
          type="submit" 
          disabled={isSubmitting}
          onClick={form.handleSubmit(onSubmit)}
          className="flex items-center gap-2 bg-[#072a45] hover:bg-[#072a45] dark:bg-[#d4af37] dark:hover:bg-[#d4af37] text-white dark:text-[#072a45]"
        >
          {isSubmitting ? "Salvando..." : client ? "Atualizar Cliente" : "Cadastrar Cliente"}
        </Button>
      </div>
    </div>
  );
}
